import React from 'react';
import { Row, Button, message, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { PaperClipOutlined } from '@ant-design/icons';

import FileTileView from '../../../../common/files/FileTileView/FileTileView';
import LiveFeedFileUpload from '../../../../dashboard/LiveFeed/LiveFeedFileUpload';
import { addFileToFileMap } from '../../../../files/state/files.actions';
import { uuid } from '../../../../helpers/helpers';
import CustomConfirmModal from '../../../../common/modals/CustomConfirmModal';
import useToggle from '../../../../common/hooks/useToggle';

const TABLE_ATTACHMENT_MAX_COUNT = 3;

function TableAttachmentCell({
  value = [],
  isDisplay,
  onChange,
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  attachmentCellId,
  id: fieldId,
  rowId,
  previewProps = {},
  responding,
}) {
  const dispatch = useDispatch();
  const { onFileClick: onClick } = previewProps;

  const list = Array.isArray(value) ? value : [];
  const responseFiles = [...list];

  const { toggle, isToggled } = useToggle();

  const onAttachDelete = (idx) => {
    const newFiles = [...list];
    newFiles.splice(idx, 1);
    onChange(newFiles);
  };

  const onAddFile = async (file) => {
    const fileObj = file;
    fileObj.autoSaveId = uuid();
    fileObj.rotation = 0;

    if (!file.existing) {
      Object.defineProperty(fileObj, 'name', {
        writable: true,
        value: `${DateTime.local().toMillis()}-${fileObj.name}`,
      });
    }

    const newFiles = [...list, fileObj];
    if (newFiles.length > TABLE_ATTACHMENT_MAX_COUNT) {
      message.warning(`You can only add up to ${TABLE_ATTACHMENT_MAX_COUNT} attachments`);
      return;
    }

    dispatch(addFileToFileMap(fileObj.autoSaveId, fileObj));
    onChange(newFiles);
  };

  const onFileClick = (index, file) => {
    if (!isDisplay && responding) {
      return CustomConfirmModal({
        title: 'File Actions',
        okText: 'View',
        cancelText: 'Delete',
        onOk: () => {
          const fileToOpen = file ?? responseFiles[index];
          if (setOpenFileIndex) setOpenFileIndex(index);
          if (setCurrFileFieldId && fieldId) setCurrFileFieldId(fieldId);
          if (setCurrAttachmentRowId && rowId != null) setCurrAttachmentRowId(rowId);
          if (onOpenFile) onOpenFile(fileToOpen);
          if (setCurrFieldId && attachmentCellId) setCurrFieldId(attachmentCellId);
        },
        onCancel: (props) => {
          if (props?.triggerCancel) return true;
          onAttachDelete(index);
          Modal.destroyAll();
        },
      })
    }

    if (onClick) {
      onClick(rowId, index);
    }
  };

  const showButton = !isDisplay && list.length < TABLE_ATTACHMENT_MAX_COUNT;

  return (
    <div>
      <Row>
        {showButton && (
          <Button
            type="button"
            className="form-attachment-add-button-redesign form-table-attachment"
            onClick={toggle}
            style={{ marginRight: 8 }}
          >
            <div className="form-attachment-add-button-center">
              <div className="file-tile-download-icon">
                <PaperClipOutlined />
              </div>
            </div>
          </Button>
        )}
        <div style={{ pointerEvents: 'all', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <FileTileView
            files={responding ? responseFiles : list}
            onClick={onFileClick}
            dimensions={40}
            titleAsTooltip
            containerStyle={{ height: 40, width: 40, padding: 0, margin: 0 }}
            shouldImageCoverContainer
          />
        </div>
      </Row>
      <LiveFeedFileUpload
        visible={isToggled}
        onUploadEnd={toggle}
        addFile={onAddFile}
        customProps={{
          width: 600,
          zIndex: 1001,
          style: {},
        }}
        placement="right"
      />
    </div>
  );
}

TableAttachmentCell.propTypes = {
  value: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    PropTypes.shape({}),
  ])),
  isDisplay: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func,
  setCurrFileFieldId: PropTypes.func,
  setCurrAttachmentRowId: PropTypes.func,
  setOpenFileIndex: PropTypes.func,
  setCurrFieldId: PropTypes.func,
  attachmentCellId: PropTypes.string,
  id: PropTypes.string,
  rowId: PropTypes.string,
  previewProps: PropTypes.shape({
    onFileClick: PropTypes.func,
  }),
  responding: PropTypes.bool,
};

TableAttachmentCell.defaultProps = {
  value: [],
  isDisplay: false,
  onOpenFile: undefined,
  setCurrFileFieldId: undefined,
  setCurrAttachmentRowId: undefined,
  setOpenFileIndex: undefined,
  setCurrFieldId: undefined,
  attachmentCellId: undefined,
  id: undefined,
  rowId: undefined,
  previewProps: {},
  responding: undefined,
};

export default TableAttachmentCell;
